import React from "react";
import { Img, Line, Text } from "components";
import { useTranslation } from "react-i18next";
import * as CUSTOM from "../../../../utils/helper/custom";

const RecipientDetail = ({ details, orderSummary, couponData, couponCode, setCouponCode, couponCodeError, applyCouponCode, removeCouponCode }) => {
  const { t } = useTranslation(); 
  return (
    <>
      <div className="flex flex-col w-full gap-0">

        {/* ── Product item ── */}
        {details?.orderDetails?.length > 0 && (
          <div className="flex flex-col gap-3 mb-4">
            {details.orderDetails.map((item, index) => (
              <div key={index} className="flex flex-row items-center gap-3">
                {/* Product image */}
                <div className="h-14 w-14 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                  <img
                    src={CUSTOM.getImage(
                      item?.orderItemDetails?.productUom?.productUomImages?.[0]?.image ||
                      item?.orderItemDetails?.imageLinks?.[0] || ""
                    )}
                    className="h-full w-full object-cover"
                    alt="product"
                  />
                </div>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <Text className="text-sm font-nunitomedium text-black-900 truncate">
                    {CUSTOM.getdataByLangKey(item?.orderItemDetails?.product?.productLocales, "", "name") ||
                     `${t("cake") || "Cake"} - ${item?.orderItemDetails?.productUom?.name || ""}`}
                  </Text>
                  <Text className="text-xs font-nunitoregular text-gray-500 truncate">
                    {item?.orderItemDetails?.productUom?.name || ""}
                  </Text>
                  <Text className="text-sm font-nunitomedium text-black-900">
                    {t("sar")} {CUSTOM.setPrice(item?.totalAmount || item?.priceDetails?.total_amount || "")}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Address row ── */}
        {details?.addressDetails?.address && (
          <div className="flex flex-row items-start gap-3 py-3 border-t border-gray-100">
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Img className="h-5 w-5" src="/images/location_on.svg" alt="location" />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <Text className="text-sm font-nunitomedium text-black-900 leading-snug">
                {details?.addressDetails?.address}
              </Text>
            </div>
          </div>
        )}

        {/* ── Recipient name / phone / occasion row ── */}
        {details?.giftDetails?.recipient_name && (
          <div className="flex flex-row items-start gap-3 py-3 border-t border-gray-100">
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Img className="h-5 w-5" src="/images/person.svg" alt="person" />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <Text className="text-sm font-nunitomedium text-black-900">
                {details?.giftDetails?.recipient_name}
              </Text>
              <Text className="text-xs font-nunitoregular text-gray-500 break-all">
                {details?.giftDetails?.mobile_no
                  ? CUSTOM.replaceKeyValue(details.giftDetails.mobile_no, "-", " ")
                  : ""}
                {details?.giftDetails?.occasion_name ? ` · ${details.giftDetails.occasion_name}` : ""}
              </Text>
            </div>
          </div>
        )}

        {/* ── Date & Time row ── */}
        {details?.deliveryDate && (
          <div className="flex flex-row items-center gap-3 py-3 border-t border-gray-100">
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Img className="h-5 w-5" src="/images/event_note.svg" alt="calendar" />
            </div>
            <Text className="text-sm font-nunitomedium text-black-900">
              {CUSTOM.changeDateFormat(details?.deliveryDate, "dd/MM/yyyy")}{" "}
              {details?.timeSlotDetails
                ? CUSTOM.formatTimeRange(details.timeSlotDetails?.openTime, details.timeSlotDetails?.closeTime)
                : ""}
            </Text>
          </div>
        )}

        {/* ── Coupon code input ── */}
        {applyCouponCode && (
          <>
            <div className="mt-3 flex flex-row items-center border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex flex-row items-center flex-1 px-3 gap-2">
                <Img className="h-5 w-5 flex-shrink-0 opacity-50" src="/images/img_iconamoondiscountlight.svg" alt="coupon" />
                <input
                  className="flex-1 text-sm font-nunitoregular text-gray-700 placeholder:text-gray-400 py-3 outline-none bg-transparent"
                  placeholder={t("couponCode") || "Enter coupon code"}
                  value={details?.couponCode || couponCode || ""}
                  disabled={!!details?.couponCode || (couponData?.isApplied)}
                  onChange={(e) => { if (setCouponCode) setCouponCode(e.target.value); }}
                />
              </div>
              {(details?.couponCode || couponData?.isApplied) ? (
                <button
                  type="button"
                  onClick={() => { if (couponData?.isApplied && typeof removeCouponCode === "function") removeCouponCode(); }}
                  className="px-4 py-3 bg-red-600 text-white-A700 text-sm font-nunitomedium hover:bg-red-700 transition-colors flex-shrink-0"
                >
                  {t("Remove") || "Remove"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { if (applyCouponCode && couponCode) applyCouponCode(); }}
                  className="px-4 py-3 bg-pink-800 text-white-A700 text-sm font-nunitomedium hover:bg-pink-900 transition-colors flex-shrink-0"
                >
                  {t("apply") || "Apply"}
                </button>
              )}
            </div>
            {couponCodeError && (
              <Text className="text-xs text-red-500 font-nunitoregular mt-1">{couponCodeError}</Text>
            )}
          </>
        )}

        {/* ── Order Summary ── */}
        <div className="mt-4 flex flex-col gap-0">
          <Text className="text-sm font-nunitobold text-black-900 mb-3">
            {t("orderSummary") || "Order Summary"}
          </Text>

          <div className="flex flex-row items-center justify-between py-2 border-t border-gray-100">
            <Text className="text-sm font-nunitoregular text-gray-600">{t("subtotal") || "Subtotal"}</Text>
            <Text className="text-sm font-nunitoregular text-black-900">
              {t("sar")} {CUSTOM.setPrice(details?.priceDetails?.sub_total || "")}
            </Text>
          </div>

          {details?.priceDetails?.vat ? (
            <div className="flex flex-row items-center justify-between py-2 border-t border-gray-100">
              <Text className="text-sm font-nunitoregular text-gray-600">{t("taxVAT") || "Tax (VAT 15%)"}</Text>
              <Text className="text-sm font-nunitoregular text-black-900">
                {t("sar")} {CUSTOM.setPrice(details?.priceDetails?.vat)}
              </Text>
            </div>
          ) : null}

          {details?.priceDetails?.delivery_fees ? (
            <div className="flex flex-row items-center justify-between py-2 border-t border-gray-100">
              <Text className="text-sm font-nunitoregular text-gray-600">{t("deliveryFee")}</Text>
              <Text className="text-sm font-nunitoregular text-black-900">
                {t("sar")} {CUSTOM.setPrice(details?.priceDetails?.delivery_fees)}
              </Text>
            </div>
          ) : null}

          {((couponData?.isApplied && couponData?.discount > 0) || (details?.priceDetails?.coupon_discount > 0)) ? (
            <div className="flex flex-row items-center justify-between py-2 border-t border-gray-100">
              <Text className="text-sm font-nunitoregular text-gray-600">{t("couponDiscount")}</Text>
              <Text className="text-sm font-nunitoregular text-teal-400">
                - {t("sar")} {CUSTOM.setPrice(couponData?.discount || details?.priceDetails?.coupon_discount)}
              </Text>
            </div>
          ) : null}

          {/* Wallet balance row if wallet used */}
          {details?.priceDetails?.wallet_balance !== undefined ? (
            <div className="flex flex-row items-center justify-between py-2 border-t border-gray-100">
              <Text className="text-sm font-nunitoregular text-gray-600">{t("wallet") || "Wallet Balance"}</Text>
              <Text className="text-sm font-nunitoregular text-teal-400">
                - {t("sar")} {CUSTOM.setPrice(details?.priceDetails?.wallet_balance)}
              </Text>
            </div>
          ) : null}

          {/* Total */}
          <div className="flex flex-row items-center justify-between py-3 border-t border-gray-200 mt-1">
            <Text className="text-base font-nunitobold text-black-900">{t("total") || "Total"}</Text>
            <Text className="text-base font-nunitobold text-black-900">
              {t("sar")} {CUSTOM.setPrice(details?.priceDetails?.total_amount || 0)}
            </Text>
          </div>
        </div>
      </div>

      {/*
        <div className="border flex flex-col items-center justify-start max-w-[331px] min-w-[331px] w-full px-8 py-5">
          <div className="relative w-full flex flex-col gap-5">
            <div className="flex flex-col items-center justify-center">
              <Img className="h-auto w-[122px]" src="/images/money-added-icon.svg" alt="checkmark" />
              <Text className="mt-3 text-xl md:text-[22px] text-black-900 text-center sm:text-lg" size="txtnunitoSemiBold24">
                {t("Recipient Details")}
              </Text>
            </div>
            ... old recipient detail rows with dashed vertical line ...
          </div>
        </div>
      */}
    </>
  );
};

export default RecipientDetail;
